import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ParameterCard from "../components/ParameterCard";
import { ContentApi } from "../../../../shared/api/contentApi";
import PaginationFooter from "../../../../components/ui/PaginationFooter";
import ParameterCreateDialog from "../components/ParameterCreateDialog";
import { sortItems } from "../../../../shared/helpers/SortByName";
import { useContentContext } from "../../ContentContext";
import { notify } from "shared/utils/notify";
import { resolvePagedTotals } from "../../../../shared/helpers/pagination";
import {Flex, Button, Select, Modal, Input} from "antd";

const SORT_FIELDS = [
    { value: "name", label: "Имя" },
    { value: "systemName", label: "System Name" },
    { value: "valueTypeCode", label: "Тип" },
];

export default function ParametersPage() {
    const navigate = useNavigate();
    const { canMutate, selectedVersion, projectId, versionId } = useContentContext();
    const [q, setQ] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState("asc");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(6);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [items, setItems] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [createOpen, setCreateOpen] = useState(false);

    // когда меняется поиск или pageSize — сбрасываем страницу на 1
    useEffect(() => {
        setPage(1);
    }, [q, pageSize]);

    useEffect(() => {
        if (!projectId || !versionId) {
            setItems([]);
            setTotal(0);
            setTotalPages(1);
            setLoading(false);
            return;
        }

        const ac = new AbortController();
        setLoading(true);
        setError("");

        ContentApi.getParametersPaged({ page, pageSize, search: q || undefined }, { signal: ac.signal })
            .then((data) => {
                setItems(Array.isArray(data?.items) ? data.items : []);
                const paging = resolvePagedTotals(data, pageSize);
                setTotal(paging.total);
                setTotalPages(paging.totalPages);
            })
            .catch((e) => !ac.signal.aborted && setError(e.message || "Ошибка загрузки"))
            .finally(() => !ac.signal.aborted && setLoading(false));

        return () => ac.abort();
    }, [page, pageSize, q, projectId, versionId]);

    // сортируем текущую страницу
    const sortedItems = useMemo(
        () => sortItems(items, sortBy, sortDir),
        [items, sortBy, sortDir]
    );

    const safePage = Math.min(page, totalPages);

    return (
        <section className="p-4 md:p-6 space-y-4">
            <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="space-y-1">
                    <h1 className="text-xl font-semibold">
                        Параметры
                    </h1>
                    <p className="text-sm text-zinc-200">
                        Данные с SbContentAPI
                    </p>
                </div>
                <button
                    type="button"
                    className="btn btn-soft-accent"
                    onClick={() => navigate(`/content/parameters`)}
                >
                    Таблица
                </button>
                <button
                    type="button"
                    className="btn btn-soft-accent"
                    onClick={() => setCreateOpen(true)}
                    disabled={!canMutate}
                >
                    Создать параметр
                </button>
                <div className="flex flex-wrap gap-2 items-end">
                    <div className="flex flex-col">
                        <label className="eyebrow mb-1" htmlFor="parameter-search">
                            Поиск
                        </label>
                        <Input
                            id="parameter-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Имя, systemName или тип…"
                            name="parameter-search"
                            className="input w-full"
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className="eyebrow mb-1" htmlFor="parameter-sort-by">
                            Сортировать по
                        </label>
                        <Select
                            id="parameter-sort-by"
                            value={sortBy}
                            onChange={setSortBy}
                            className="w-full"
                        >
                            {SORT_FIELDS.map((f) => (
                                <Select.Option key={f.value} value={f.value}>
                                    {f.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>

                    <div className="flex flex-col">
                        <label className="eyebrow mb-1" htmlFor="parameter-sort-dir">
                            Порядок
                        </label>
                        <Select
                            id="parameter-sort-dir"
                            value={sortDir}
                            onChange={setSortDir}
                            className="w-full"
                        >
                            <Select.Option value="asc">По возрастанию</Select.Option>
                            <Select.Option value="desc">По убыванию</Select.Option>
                        </Select>
                    </div>

                    <div className="flex flex-col">
                        <label className="eyebrow mb-1" htmlFor="parameter-page-size">
                            На странице
                        </label>
                        <Select
                            id="parameter-page-size"
                            value={String(pageSize)}
                            onChange={(val) => setPageSize(Number(val))}
                            className="w-full"
                        >
                            {[6, 12, 18, 24].map((n) => (
                                <Select.Option key={n} value={String(n)}>
                                    {n}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                </div>
            </header>

            {error && (
                <div className="rounded-xl border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
                    {error}
                </div>
            )}

            {!canMutate && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-700 p-3 text-sm">
                    Текущая версия ({selectedVersion?.status || "unknown"}) только для чтения. Используйте Fork в draft для изменений.
                </div>
            )}

            {loading ? (
                <div className="p-6 text-sm text-zinc-500">Загрузка…</div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {sortedItems.map((p) => (
                            <ParameterCard
                                key={p.id || p.parameterId}
                                parameter={p}
                                onOpen={(param) => navigate(`/content/parameters/${param.id || param.parameterId}`)}
                                onCopy={(param) => notify.wip(`Копирование «${param.systemName}»`)}
                            />
                        ))}
                    </div>
                    <PaginationFooter
                        total={total}
                        page={safePage}
                        totalPages={totalPages}
                        onPageChange={setPage}
                    />
                </>
            )}

            <ParameterCreateDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={() => {
                    setCreateOpen(false);
                    setPage(1);
                }}
                projectId={projectId}
                versionId={versionId}
            />
        </section>
    );
}
